import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Beaker,
  Sparkles,
  Calendar,
  CheckCircle2,
  PlayCircle,
  Settings as SettingsIcon,
  MessageSquare,
  BookOpen,
  AlertTriangle,
} from 'lucide-react';
import { getAllFeatureModes, setFeatureMode } from '@/lib/data-service';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';

interface Feature {
  id: string;
  name: string;
  description: string;
  category: 'preview' | 'coming-soon' | 'live';
  icon: React.ReactNode;
  availableDate?: string;
  route?: string;
}

const features: Feature[] = [
  {
    id: 'error-handling-demo',
    name: 'Error Handling System',
    description: 'World-class error handling with user-friendly UX and technical details',
    category: 'live',
    icon: <AlertTriangle className="h-5 w-5" />,
    route: 'error-handling-demo',
  },
  {
    id: 'column-configurator-demo',
    name: 'Column Configurator',
    description: 'Customize project list columns with drag-and-drop, presets, and filters',
    category: 'live',
    icon: <SettingsIcon className="h-5 w-5" />,
    route: 'column-configurator-demo',
  },
  {
    id: 'responsive-table-demo',
    name: 'Responsive Tables',
    description: 'Horizontal scrolling tables with visual indicators and custom scrollbars',
    category: 'live',
    icon: <CheckCircle2 className="h-5 w-5" />,
    route: 'responsive-table-demo',
  },
  {
    id: 'data-table-demo',
    name: 'DataTableEnhanced',
    description: 'Three-tier grid architecture demo: Page > Grid > DataTableEnhanced with sorting, pagination, and column config',
    category: 'live',
    icon: <Sparkles className="h-5 w-5 text-info-600" />,
    route: 'data-table-demo',
  },
  {
    id: 'dashboard',
    name: 'Homepage',
    description: 'Project manager and executive homepage with real-time KPIs',
    category: 'live',
    icon: <CheckCircle2 className="h-5 w-5" />,
  },
  {
    id: 'projects',
    name: 'Projects',
    description: 'Comprehensive project list with filtering and search',
    category: 'live',
    icon: <CheckCircle2 className="h-5 w-5" />,
  },
  {
    id: 'utilisation',
    name: 'Utilisation Tracking',
    description: 'Track team billable hours and resource capacity',
    category: 'live',
    icon: <CheckCircle2 className="h-5 w-5" />,
  },
  {
    id: 'wip-reporting',
    name: 'Work in Progress Analysis',
    description: 'Run month-end WIP reports and track unbilled work',
    category: 'preview',
    icon: <Sparkles className="h-5 w-5 text-info-600" />,
  },
  {
    id: 'profitability',
    name: 'Project Profitability',
    description: 'Real-time project margin and profitability analysis',
    category: 'preview',
    icon: <Sparkles className="h-5 w-5 text-info-600" />,
  },
  {
    id: 'budgets',
    name: 'Budget Variance Analysis',
    description: 'Track budget vs actuals with variance alerts',
    category: 'preview',
    icon: <Sparkles className="h-5 w-5 text-info-600" />,
  },
  {
    id: 'resources',
    name: 'Resource Allocation',
    description: 'Assign resources to projects with conflict detection',
    category: 'preview',
    icon: <Sparkles className="h-5 w-5 text-info-600" />,
  },
  {
    id: 'gantt',
    name: 'Gantt Charts & Dependencies',
    description: 'Visual project planning with interactive timeline',
    category: 'coming-soon',
    icon: <Calendar className="h-5 w-5 text-purple-600" />,
    availableDate: 'Q2 2025',
  },
  {
    id: 'portfolio',
    name: 'Portfolio Management',
    description: 'Executive view across all projects with health scoring',
    category: 'coming-soon',
    icon: <Calendar className="h-5 w-5 text-purple-600" />,
    availableDate: 'Q3 2025',
  },
  {
    id: 'ai-generation',
    name: 'AI-Powered Project Generation',
    description: 'Generate project structure from natural language descriptions',
    category: 'coming-soon',
    icon: <Calendar className="h-5 w-5 text-purple-600" />,
    availableDate: 'Q3 2025',
  },
];

export function FeatureLabsPage({ onNavigate, showHeader = true }: { onNavigate?: (page: string) => void; showHeader?: boolean } = {}) {
  const navigateHook = useNavigate();
  const handleNavigate = onNavigate || ((page: string) => navigateHook(`/${page}`));
  const { preferences, updatePreferences } = useUserPreferences();
  const [enableDialog, setEnableDialog] = useState<{ open: boolean; feature: Feature | null }>({
    open: false,
    feature: null,
  });
  const [switchDialog, setSwitchDialog] = useState<{ open: boolean; feature: Feature | null }>({
    open: false,
    feature: null,
  });

  const featureModes = getAllFeatureModes();

  const handleEnableLive = (feature: Feature) => {
    setFeatureMode(feature.id, 'live');
    setEnableDialog({ open: false, feature: null });
  };

  const handleSwitchToDemo = (feature: Feature) => {
    setFeatureMode(feature.id, 'demo');
    setSwitchDialog({ open: false, feature: null });
  };

  const previewFeatures = features.filter(f => f.category === 'preview');
  const comingSoonFeatures = features.filter(f => f.category === 'coming-soon');
  const liveFeatures = features.filter(f => f.category === 'live');

  const FeatureCard = ({ feature }: { feature: Feature }) => {
    const mode = featureModes[feature.id] || 'demo';
    const isLive = mode === 'live';

    return (
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-1">{feature.icon}</div>
              <div>
                <CardTitle className="text-base">{feature.name}</CardTitle>
                <CardDescription className="mt-1">{feature.description}</CardDescription>
              </div>
            </div>
            {feature.category === 'preview' && (
              <Badge variant="info">
                {isLive ? 'Live' : 'Demo'}
              </Badge>
            )}
            {feature.category === 'coming-soon' && (
              <Badge variant="accent" accent="purple">
                {feature.availableDate}
              </Badge>
            )}
            {feature.category === 'live' && (
              <Badge variant="success">
                Live & Enabled
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {feature.category === 'preview' && (
              <>
                {!isLive ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => feature.route && handleNavigate(feature.route || feature.id)}
                    >
                      <PlayCircle className="mr-1 h-4 w-4" />
                      Try Demo
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setEnableDialog({ open: true, feature })}
                    >
                      Enable Live Mode →
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      onClick={() => feature.route && handleNavigate(feature.route || feature.id)}
                    >
                      Open Feature
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSwitchDialog({ open: true, feature })}
                    >
                      <SettingsIcon className="mr-1 h-4 w-4" />
                      Settings
                    </Button>
                  </>
                )}
              </>
            )}
            {feature.category === 'coming-soon' && (
              <>
                <Button size="sm" variant="outline">
                  <PlayCircle className="mr-1 h-4 w-4" />
                  Preview Demo
                </Button>
                <Button size="sm" variant="outline">
                  <BookOpen className="mr-1 h-4 w-4" />
                  Learn More
                </Button>
              </>
            )}
            {feature.category === 'live' && (
              <Button
                size="sm"
                onClick={() => feature.route && handleNavigate(feature.route || feature.id)}
              >
                Open Feature
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {showHeader && (
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Beaker className="h-6 w-6 text-info-600" />
              <h2 className="text-2xl font-bold">Feature Labs</h2>
            </div>
            <p className="text-muted-foreground">
              Explore upcoming features and enable new capabilities
            </p>
          </div>
          <Button variant="outline">
            <MessageSquare className="mr-2 h-4 w-4" />
            Send Feedback
          </Button>
        </div>
      )}



      {/* Preview Features */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-info-600" />
          <h3 className="text-lg font-semibold">Preview Features</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {previewFeatures.map(feature => (
            <FeatureCard key={feature.id} feature={feature} />
          ))}
        </div>
      </div>

      <Separator />

      {/* Coming Soon */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold">Coming Soon</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {comingSoonFeatures.map(feature => (
            <FeatureCard key={feature.id} feature={feature} />
          ))}
        </div>
      </div>

      <Separator />

      {/* Live & Enabled */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="h-5 w-5 text-success-600" />
          <h3 className="text-lg font-semibold">Live & Enabled</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {liveFeatures.map(feature => (
            <FeatureCard key={feature.id} feature={feature} />
          ))}
        </div>
      </div>

      {/* Enable Live Mode Dialog */}
      <AlertDialog
        open={enableDialog.open}
        onOpenChange={open => setEnableDialog({ open, feature: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable {enableDialog.feature?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>You're about to enable {enableDialog.feature?.name} with live data.</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-success-600 mt-0.5">✓</span>
                  <span>This will use your real project data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success-600 mt-0.5">✓</span>
                  <span>Changes you make will be saved to the database</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success-600 mt-0.5">✓</span>
                  <span>Reports will reflect actual financial data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-success-600 mt-0.5">✓</span>
                  <span>You can switch back to Demo Mode anytime</span>
                </li>
              </ul>
              <p className="font-medium">Ready to start using {enableDialog.feature?.name}?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => enableDialog.feature && handleEnableLive(enableDialog.feature)}
            >
              Yes, Enable Live Mode
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Switch to Demo Dialog */}
      <AlertDialog
        open={switchDialog.open}
        onOpenChange={open => setSwitchDialog({ open, feature: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to Demo Mode?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Switch {switchDialog.feature?.name} back to Demo Mode to use sample data instead of
                your real project data.
              </p>
              <p className="text-sm">
                This is useful for training new team members or demonstrating features without
                affecting your live data.
              </p>
              <p className="font-medium">You can switch back to Live Mode anytime.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => switchDialog.feature && handleSwitchToDemo(switchDialog.feature)}
            >
              Switch to Demo Mode
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
